import { useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import { Link } from 'react-router-dom';
import {
    Archive,
    BarChart3,
    ChevronDown,
    ChevronRight,
    Copy,
    GripVertical,
    Loader2,
    Pause,
    Pencil,
    Play,
    Plus,
    Search,
    Upload,
} from 'lucide-react';
import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
    type DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    useClientPrograms,
    useCreateClientProgram,
    useUpdateClientProgram,
    useRetireClientProgram,
    useSaveClientProgramToBank,
    type ClientProgram,
    type ClientProgramStatus,
    type ClientProgramWritePayload,
} from '@/api/clientPrograms';
import {
    useClientProgramTargets,
    useCreateClientProgramTarget,
    useUpdateClientProgramTarget,
    useReorderClientProgramTargets,
    useRetireClientProgramTarget,
    useSaveClientProgramTargetToBank,
    type ClientProgramTarget,
    type ClientProgramTargetWritePayload,
    type ClientTargetStatus,
} from '@/api/clientProgramTargets';
import {
    useProgramBank,
    useCopyProgramBankToClient,
    TEACHING_METHODS,
    type ChainingMethod,
    type MasteryDirection,
    type ProgramBankEntry,
    type TargetDataType,
} from '@/api/programBank';
import { useProgramDomains } from '@/api/programDomains';
import { useAuthStore } from '@/stores/auth';
import type { ClientRecord } from '@/api/clients';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { StatusBadge } from '@/components/ui/Badge';
import { Alert } from '@/components/ui/Alert';
import { FormField, Input, Textarea } from '@/components/ui/FormField';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { toIsoDate } from '@/lib/calendar';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/Dialog';

/*
 * Client Programs tab
 *
 * Programs live on the client and are independent of plans. Each program
 * contains an ordered list of targets; targets are the unit that accrues
 * data during sessions.
 *
 * Creation paths mirror behaviors: copy-from-bank (links sourceBankId for
 * later save-to-bank) or direct create (unlinked, promote later).
 *
 * Discontinuing a program cascades to its active targets on the server.
 * We surface that in the confirmation copy so clinicians aren't surprised.
 *
 * Target save-to-bank has two extra error paths beyond behaviors:
 *   400 parentNotLinked    — the parent program isn't linked to the bank yet
 *   409 parentBankArchived — the parent's bank entry is archived; restore it
 */

const STATUS_FILTERS: Array<{ id: ClientProgramStatus | 'All'; label: string }> = [
    { id: 'Active', label: 'Active' },
    { id: 'Planned', label: 'Planned' },
    { id: 'On Hold', label: 'On Hold' },
    { id: 'Mastered', label: 'Mastered' },
    { id: 'Discontinued', label: 'Discontinued' },
    { id: 'All', label: 'All' },
];

export function ClientProgramsTab({ client: c }: { client: ClientRecord }) {
    const canEdit = useAuthStore((s) => s.hasPermission('edit_treatment_plans'));
    const [statusFilter, setStatusFilter] = useState<ClientProgramStatus | 'All'>('Active');

    const listOpts = statusFilter === 'All'
        ? { clientId: c.id, includeRetired: true }
        : { clientId: c.id, status: statusFilter };
    const { data, isLoading, isError, error } = useClientPrograms(listOpts);

    const createM = useCreateClientProgram(c.id);
    const updateM = useUpdateClientProgram(c.id);
    const retireM = useRetireClientProgram(c.id);
    const saveM = useSaveClientProgramToBank(c.id);

    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState<ClientProgram | null>(null);
    const [copying, setCopying] = useState(false);
    const [savingToBank, setSavingToBank] = useState<ClientProgram | null>(null);
    const [confirmRetire, setConfirmRetire] = useState<ClientProgram | null>(null);
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});

    const { data: domains } = useProgramDomains();
    const activeDomains = useMemo(
        () => (domains ?? [])
            .filter((d) => d.status === 'Active')
            .sort((a, b) => a.name.localeCompare(b.name)),
        [domains],
    );

    function toggleExpand(id: string) {
        setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <h2 className="font-display text-lg font-bold text-text">Programs</h2>
                {canEdit && (
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setCopying(true)}>
                            <Copy className="w-3.5 h-3.5" /> Copy from bank
                        </Button>
                        <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
                            <Plus className="w-3.5 h-3.5" /> Add Program
                        </Button>
                    </div>
                )}
            </div>

            <Card className="p-0 overflow-hidden">
                <div className="flex items-center gap-1 p-2 border-b border-border">
                    {STATUS_FILTERS.map((s) => (
                        <button
                            key={s.id}
                            type="button"
                            onClick={() => setStatusFilter(s.id)}
                            className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${statusFilter === s.id
                                    ? 'bg-surface-muted text-text'
                                    : 'text-text-muted hover:text-text hover:bg-surface-muted'
                                }`}
                            aria-pressed={statusFilter === s.id}
                        >
                            {s.label}
                        </button>
                    ))}
                    <div className="ml-auto text-xs text-text-subtle tabular-nums">
                        {(data?.length ?? 0)} {(data?.length ?? 0) === 1 ? 'program' : 'programs'}
                    </div>
                </div>

                {isLoading ? (
                    <div className="p-12 flex items-center justify-center text-text-subtle">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
                    </div>
                ) : isError ? (
                    <div className="p-12 text-center text-error">
                        <p className="font-medium mb-1">Couldn't load programs</p>
                        <p className="text-xs text-text-subtle">
                            {extractErrorMessage(error, 'Please try again.')}
                        </p>
                    </div>
                ) : !data || data.length === 0 ? (
                    <div className="p-12 text-center text-text-subtle">
                        <p>
                            {statusFilter === 'Active'
                                ? 'No active programs for this client yet.'
                                : 'No programs in this view.'}
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                    <th className="px-2 py-2 w-6"></th>
                                    <th className="px-3 py-2 font-bold">Name</th>
                                    <th className="px-3 py-2 font-bold">Domain</th>
                                    <th className="px-3 py-2 font-bold">Teaching</th>
                                    <th className="px-3 py-2 font-bold">Status</th>
                                    <th className="px-3 py-2 font-bold">Source</th>
                                    {canEdit && <th className="px-3 py-2 font-bold w-36 text-right">Actions</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((p) => (
                                    <ProgramRow
                                        key={p.id}
                                        program={p}
                                        clientId={c.id}
                                        canEdit={canEdit}
                                        expanded={!!expanded[p.id]}
                                        onToggle={() => toggleExpand(p.id)}
                                        onEdit={() => setEditing(p)}
                                        onSaveToBank={() => setSavingToBank(p)}
                                        onSetStatus={(status) =>
                                            updateM.mutateAsync({ id: p.id, payload: { status } })
                                        }
                                        onRetire={() => setConfirmRetire(p)}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {(creating || editing) && (
                <ProgramFormDialog
                    initial={editing}
                    activeDomains={activeDomains}
                    onClose={() => { setCreating(false); setEditing(null); }}
                    onSubmit={async (payload) => {
                        if (editing) await updateM.mutateAsync({ id: editing.id, payload });
                        else await createM.mutateAsync(payload);
                    }}
                />
            )}

            {copying && (
                <CopyProgramFromBankDialog
                    clientId={c.id}
                    onClose={() => setCopying(false)}
                />
            )}

            {savingToBank && (
                <SaveProgramToBankDialog
                    program={savingToBank}
                    onClose={() => setSavingToBank(null)}
                    onSubmit={async (createNew) => {
                        await saveM.mutateAsync({ id: savingToBank.id, createNew });
                    }}
                />
            )}

            {confirmRetire && (
                <RetireProgramDialog
                    program={confirmRetire}
                    onClose={() => setConfirmRetire(null)}
                    onConfirm={async () => {
                        await retireM.mutateAsync(confirmRetire.id);
                        setConfirmRetire(null);
                    }}
                />
            )}
        </div>
    );
}

// ─── program row with expandable target list ────────────────────────────────

function ProgramRow({
    program,
    clientId,
    canEdit,
    expanded,
    onToggle,
    onEdit,
    onSaveToBank,
    onSetStatus,
    onRetire,
}: {
    program: ClientProgram;
    clientId: string;
    canEdit: boolean;
    expanded: boolean;
    onToggle: () => void;
    onEdit: () => void;
    onSaveToBank: () => void;
    onSetStatus: (status: ClientProgramStatus) => Promise<unknown>;
    onRetire: () => void;
}) {
    const retired = program.status === 'Discontinued' || program.status === 'Mastered';
    const onHold = program.status === 'On Hold';
    const planned = program.status === 'Planned';
    const colSpan = canEdit ? 7 : 6;

    return (
        <>
            <tr
                className={`border-b border-border align-top transition-colors cursor-pointer ${retired ? 'opacity-60 hover:bg-surface-muted' : 'hover:bg-surface-muted'
                    }`}
                onClick={onToggle}
            >
                <td className="px-2 py-2">
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onToggle(); }}
                        aria-label={expanded ? `Collapse ${program.name}` : `Expand ${program.name}`}
                        aria-expanded={expanded}
                        className="p-0.5 rounded hover:bg-surface text-text-subtle hover:text-text"
                    >
                        {expanded
                            ? <ChevronDown className="w-3.5 h-3.5" />
                            : <ChevronRight className="w-3.5 h-3.5" />}
                    </button>
                </td>
                <td className="px-3 py-2 font-medium">
                    {program.name}
                    {program.description && (
                        <div className="text-xs text-text-subtle font-normal mt-0.5 line-clamp-2">
                            {program.description}
                        </div>
                    )}
                </td>
                <td className="px-3 py-2 text-text-muted whitespace-nowrap">{program.programDomain || '—'}</td>
                <td className="px-3 py-2 text-text-muted whitespace-nowrap">{program.teachingMethod || '—'}</td>
                <td className="px-3 py-2"><StatusBadge status={program.status} /></td>
                <td className="px-3 py-2 text-xs text-text-subtle whitespace-nowrap">
                    {program.sourceBankId ? 'Linked to bank' : 'Custom'}
                </td>
                {canEdit && (
                    <td className="px-3 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        {!retired && (
                            <>
                                <Button
                                    variant="ghost"
                                    size="xs"
                                    aria-label={`Edit ${program.name}`}
                                    onClick={onEdit}
                                    className="mr-1"
                                >
                                    <Pencil className="w-3 h-3" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="xs"
                                    aria-label={`Save ${program.name} to bank`}
                                    title="Save to bank"
                                    onClick={onSaveToBank}
                                    className="mr-1"
                                >
                                    <Upload className="w-3 h-3" />
                                </Button>
                                {onHold || planned ? (
                                    <Button
                                        variant="ghost"
                                        size="xs"
                                        aria-label={planned ? `Activate ${program.name}` : `Resume ${program.name}`}
                                        title={planned ? 'Activate' : 'Resume'}
                                        onClick={() => onSetStatus('Active')}
                                        className="mr-1"
                                    >
                                        <Play className="w-3 h-3" />
                                    </Button>
                                ) : (
                                    <Button
                                        variant="ghost"
                                        size="xs"
                                        aria-label={`Pause ${program.name}`}
                                        title="Pause"
                                        onClick={() => onSetStatus('On Hold')}
                                        className="mr-1"
                                    >
                                        <Pause className="w-3 h-3" />
                                    </Button>
                                )}
                                <Button
                                    variant="ghost"
                                    size="xs"
                                    aria-label={planned ? `Delete ${program.name}` : `Discontinue ${program.name}`}
                                    title={planned ? 'Delete' : 'Discontinue'}
                                    onClick={onRetire}
                                >
                                    <Archive className="w-3 h-3" />
                                </Button>
                            </>
                        )}
                    </td>
                )}
            </tr>
            {expanded && (
                <tr className="border-b border-border bg-surface-muted/40">
                    <td></td>
                    <td colSpan={colSpan - 1} className="px-3 py-3">
                        <TargetsSection program={program} canEdit={canEdit} clientId={clientId} />
                    </td>
                </tr>
            )}
        </>
    );
}

// ─── nested targets panel (one per expanded program) ────────────────────────

function TargetsSection({ program, canEdit, clientId }: { program: ClientProgram; canEdit: boolean; clientId: string }) {
    const [showRetired, setShowRetired] = useState(false);
    const { data, isLoading, isError } = useClientProgramTargets({
        clientProgramId: program.id,
        includeRetired: showRetired,
    });

    const createM = useCreateClientProgramTarget(program.id);
    const updateM = useUpdateClientProgramTarget(program.id);
    const reorderM = useReorderClientProgramTargets(program.id);
    const retireM = useRetireClientProgramTarget(program.id);
    const saveM = useSaveClientProgramTargetToBank(program.id);

    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState<ClientProgramTarget | null>(null);
    const [savingToBank, setSavingToBank] = useState<ClientProgramTarget | null>(null);
    const [confirmRetire, setConfirmRetire] = useState<ClientProgramTarget | null>(null);

    const programRetired = program.status === 'Discontinued' || program.status === 'Mastered';

    // Split into drag-ordered active set (In Progress + On Hold) and retired
    // (Mastered + Discontinued). Retired rows are pinned to the bottom and
    // excluded from drag to mirror the server-side reorder contract.
    const { activeTargets, retiredTargets, sortedTargets } = useMemo(() => {
        const all = data ?? [];
        const active = all
            .filter((t) => t.status !== 'Discontinued' && t.status !== 'Mastered')
            .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
        const retired = all
            .filter((t) => t.status === 'Discontinued' || t.status === 'Mastered')
            .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
        return { activeTargets: active, retiredTargets: retired, sortedTargets: [...active, ...retired] };
    }, [data]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    function handleDragEnd(e: DragEndEvent) {
        const { active, over } = e;
        if (!over || active.id === over.id) return;
        const oldIndex = activeTargets.findIndex((t) => t.id === active.id);
        const newIndex = activeTargets.findIndex((t) => t.id === over.id);
        if (oldIndex < 0 || newIndex < 0) return;
        const reordered = activeTargets.slice();
        const [moved] = reordered.splice(oldIndex, 1);
        reordered.splice(newIndex, 0, moved);
        reorderM.mutate(reordered.map((t) => t.id));
    }

    const canDrag = canEdit && !programRetired;

    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-bold text-text-muted uppercase tracking-wider">
                    Targets <span className="font-normal text-text-subtle">({sortedTargets.length})</span>
                </div>
                <div className="flex items-center gap-3">
                    <label className="flex items-center gap-1.5 text-xs text-text-muted select-none cursor-pointer">
                        <input
                            type="checkbox"
                            checked={showRetired}
                            onChange={(e) => setShowRetired(e.target.checked)}
                        />
                        Show retired
                    </label>
                    {canEdit && !programRetired && (
                        <Button variant="primary" size="xs" onClick={() => setCreating(true)}>
                            <Plus className="w-3 h-3" /> Add target
                        </Button>
                    )}
                </div>
            </div>

            {isLoading ? (
                <div className="py-6 flex items-center justify-center text-text-subtle text-xs">
                    <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> Loading targets…
                </div>
            ) : isError ? (
                <div className="py-6 text-center text-error text-xs">Couldn't load targets.</div>
            ) : sortedTargets.length === 0 ? (
                <div className="py-6 text-center text-text-subtle text-xs">
                    No targets defined for this program yet.
                </div>
            ) : (
                <div className="border border-border rounded-md overflow-hidden bg-background">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                {canEdit && <th className="px-2 py-1.5 w-7" aria-label="Drag to reorder" />}
                                <th className="px-3 py-1.5 font-bold">Name</th>
                                <th className="px-3 py-1.5 font-bold whitespace-nowrap">Data type</th>
                                <th className="px-3 py-1.5 font-bold">Mastery</th>
                                <th className="px-3 py-1.5 font-bold">Status</th>
                                <th className="px-3 py-1.5 font-bold">Source</th>
                                {canEdit && <th className="px-3 py-1.5 font-bold w-32 text-right">Actions</th>}
                            </tr>
                        </thead>
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext
                                items={activeTargets.map((t) => t.id)}
                                strategy={verticalListSortingStrategy}
                            >
                                <tbody>
                                    {activeTargets.map((t) => (
                                        <ClientTargetRow
                                            key={t.id}
                                            target={t}
                                            canEdit={canEdit}
                                            draggable={canDrag}
                                            clientId={clientId}
                                            programRetired={programRetired}
                                            onEdit={() => setEditing(t)}
                                            onSaveToBank={() => setSavingToBank(t)}
                                            onRetire={() => setConfirmRetire(t)}
                                        />
                                    ))}
                                    {retiredTargets.map((t) => (
                                        <ClientTargetRow
                                            key={t.id}
                                            target={t}
                                            canEdit={canEdit}
                                            draggable={false}
                                            clientId={clientId}
                                            programRetired={programRetired}
                                            onEdit={() => setEditing(t)}
                                            onSaveToBank={() => setSavingToBank(t)}
                                            onRetire={() => setConfirmRetire(t)}
                                        />
                                    ))}
                                </tbody>
                            </SortableContext>
                        </DndContext>
                    </table>
                </div>
            )}

            {(creating || editing) && (
                <TargetFormDialog
                    initial={editing}
                    onClose={() => { setCreating(false); setEditing(null); }}
                    onSubmit={async (payload) => {
                        if (editing) await updateM.mutateAsync({ id: editing.id, payload });
                        else await createM.mutateAsync(payload);
                    }}
                />
            )}

            {savingToBank && (
                <SaveTargetToBankDialog
                    target={savingToBank}
                    onClose={() => setSavingToBank(null)}
                    onSubmit={async (createNew) => {
                        await saveM.mutateAsync({ id: savingToBank.id, createNew });
                    }}
                />
            )}

            {confirmRetire && (
                <RetireTargetDialog
                    target={confirmRetire}
                    onClose={() => setConfirmRetire(null)}
                    onConfirm={async () => {
                        await retireM.mutateAsync(confirmRetire.id);
                        setConfirmRetire(null);
                    }}
                />
            )}
        </div>
    );
}

// ─── program form (create/edit) ─────────────────────────────────────────────

function ProgramFormDialog({
    initial,
    activeDomains,
    onClose,
    onSubmit,
}: {
    initial: ClientProgram | null;
    activeDomains: { id: string; name: string }[];
    onClose: () => void;
    onSubmit: (p: ClientProgramWritePayload) => Promise<void>;
}) {
    const [name, setName] = useState(initial?.name ?? '');
    const [programDomainId, setDomainId] = useState(initial?.programDomainId ?? '');
    const [teachingMethod, setTeaching] = useState(initial?.teachingMethod ?? '');
    const [description, setDescription] = useState(initial?.description ?? '');
    const [status, setStatus] = useState<ClientProgramStatus>(initial?.status ?? 'Active');
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setErr(null);
        if (!name.trim()) { setErr('Name is required'); return; }
        setSaving(true);
        try {
            await onSubmit({
                name: name.trim(),
                programDomainId: programDomainId || null,
                teachingMethod: teachingMethod.trim(),
                description: description.trim(),
                status,
            });
            onClose();
        } catch (e2) {
            setErr(extractErrorMessage(e2, 'Could not save.'));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="w-[min(calc(100vw-2rem),44rem)] max-h-[85vh] flex flex-col" aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>{initial ? 'Edit Program' : 'Add Program'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-1 -mx-1">
                    <FormField label="Name" required error={err ?? undefined}>
                        {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}
                    </FormField>
                    <div className="grid grid-cols-2 gap-x-4">
                        <FormField label="Domain">
                            {(id) => (
                                <select
                                    id={id}
                                    value={programDomainId ?? ''}
                                    onChange={(e) => setDomainId(e.target.value)}
                                    className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none"
                                >
                                    <option value="">— None —</option>
                                    {activeDomains.map((d) => (
                                        <option key={d.id} value={d.id}>{d.name}</option>
                                    ))}
                                </select>
                            )}
                        </FormField>
                        <FormField label="Teaching method">
                            {(id) => (
                                <select
                                    id={id}
                                    value={teachingMethod}
                                    onChange={(e) => setTeaching(e.target.value)}
                                    className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none"
                                >
                                    <option value="">— None —</option>
                                    {TEACHING_METHODS.map((m) => (
                                        <option key={m} value={m}>{m}</option>
                                    ))}
                                </select>
                            )}
                        </FormField>
                    </div>
                    {initial && (
                        <FormField label="Status">
                            {(id) => (
                                <select
                                    id={id}
                                    value={status}
                                    onChange={(e) => setStatus(e.target.value as ClientProgramStatus)}
                                    className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none max-w-[12rem]"
                                >
                                    <option value="Planned">Planned</option>
                                    <option value="Active">Active</option>
                                    <option value="On Hold">On Hold</option>
                                    <option value="Mastered">Mastered</option>
                                    <option value="Discontinued">Discontinued</option>
                                </select>
                            )}
                        </FormField>
                    )}
                    <FormField label="Description">
                        {(id) => <Textarea id={id} value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />}
                    </FormField>

                    <DialogFooter className="mt-2">
                        <DialogClose asChild>
                            <Button variant="ghost" size="sm" type="button">Cancel</Button>
                        </DialogClose>
                        <Button variant="primary" size="sm" type="submit" disabled={saving}>
                            {saving ? 'Saving…' : initial ? 'Save' : 'Create'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

// ─── copy program from bank ─────────────────────────────────────────────────

function CopyProgramFromBankDialog({ clientId, onClose }: { clientId: string; onClose: () => void }) {
    const [search, setSearch] = useState('');
    const [domainFilter, setDomainFilter] = useState('');
    const { data, isLoading } = useProgramBank({ programDomainId: domainFilter || undefined, includeTargets: true });
    const { data: domains } = useProgramDomains();
    const copyM = useCopyProgramBankToClient();

    const activeDomains = useMemo(
        () => (domains ?? [])
            .filter((d) => d.status === 'Active')
            .sort((a, b) => a.name.localeCompare(b.name)),
        [domains],
    );

    const rows = useMemo(() => {
        if (!data) return [];
        const q = search.trim().toLowerCase();
        if (!q) return data;
        return data.filter((r) => r.name.toLowerCase().includes(q));
    }, [data, search]);

    async function copyEntry(entry: ProgramBankEntry) {
        try {
            await copyM.mutateAsync({ id: entry.id, clientId });
            onClose();
        } catch {
            /* surfaced below */
        }
    }

    const serverError = (() => {
        const e = copyM.error;
        if (!e) return null;
        if (e instanceof AxiosError) return e.response?.data?.error ?? 'Failed to copy program';
        return 'Unexpected error';
    })();

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="w-[min(calc(100vw-2rem),48rem)] max-h-[85vh] flex flex-col" aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>Copy program from bank</DialogTitle>
                </DialogHeader>
                <div className="flex flex-wrap items-center gap-3 mb-3">
                    <div className="relative flex-1 min-w-[220px]">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-subtle" />
                        <Input
                            type="search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search programs…"
                            className="pl-8 h-8 py-1"
                        />
                    </div>
                    <select
                        value={domainFilter}
                        onChange={(e) => setDomainFilter(e.target.value)}
                        className="h-8 px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                    >
                        <option value="">All domains</option>
                        {activeDomains.map((d) => (
                            <option key={d.id} value={d.id}>{d.name}</option>
                        ))}
                    </select>
                </div>
                {serverError && <Alert variant="danger" className="mb-3">{serverError}</Alert>}
                <div className="flex-1 overflow-y-auto border border-border rounded-md">
                    {isLoading ? (
                        <div className="p-8 flex items-center justify-center text-text-subtle">
                            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading bank…
                        </div>
                    ) : rows.length === 0 ? (
                        <div className="p-8 text-center text-text-subtle text-sm">
                            {data && data.length > 0 ? 'No matches.' : 'The program bank is empty.'}
                        </div>
                    ) : (
                        <ul>
                            {rows.map((p) => {
                                const targetCount = p.targets?.length ?? 0;
                                return (
                                    <li
                                        key={p.id}
                                        className="flex items-start justify-between gap-3 px-3 py-2 border-b border-border last:border-b-0 hover:bg-surface-muted"
                                    >
                                        <div className="min-w-0 flex-1">
                                            <div className="font-medium text-sm text-text">{p.name}</div>
                                            <div className="text-xs text-text-subtle">
                                                {p.programDomain || '—'}
                                                {p.teachingMethod && <> · {p.teachingMethod}</>}
                                                <> · {targetCount} target{targetCount === 1 ? '' : 's'}</>
                                            </div>
                                            {p.description && (
                                                <div className="text-xs text-text-muted mt-0.5 line-clamp-2">{p.description}</div>
                                            )}
                                        </div>
                                        <Button
                                            variant="primary"
                                            size="xs"
                                            onClick={() => copyEntry(p)}
                                            disabled={copyM.isPending}
                                        >
                                            Copy to client
                                        </Button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="ghost" size="sm" type="button">Close</Button>
                    </DialogClose>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ─── save program to bank ───────────────────────────────────────────────────

function SaveProgramToBankDialog({
    program,
    onClose,
    onSubmit,
}: {
    program: ClientProgram;
    onClose: () => void;
    onSubmit: (createNew: boolean) => Promise<void>;
}) {
    const [working, setWorking] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const [canCreateNew, setCanCreateNew] = useState(false);

    const linked = !!program.sourceBankId;

    async function submit(createNew: boolean) {
        setErr(null);
        setWorking(true);
        try {
            await onSubmit(createNew);
            onClose();
        } catch (e) {
            if (e instanceof AxiosError) {
                const data = e.response?.data as { error?: string; canCreateNew?: boolean } | undefined;
                setErr(data?.error ?? 'Could not save to bank');
                if (data?.canCreateNew) setCanCreateNew(true);
            } else {
                setErr(extractErrorMessage(e, 'Could not save to bank'));
            }
        } finally {
            setWorking(false);
        }
    }

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>Save to bank?</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 text-sm">
                    <p className="text-text-muted">
                        {linked
                            ? <>Push the current values of <span className="font-medium text-text">{program.name}</span> to the linked bank entry, replacing what's there.</>
                            : <>This program isn't linked to the bank yet. Saving will create a new bank entry from the current values and link this program to it.</>
                        }
                    </p>
                    {linked && (
                        <Alert variant="warning" className="text-xs">
                            Other clients who already copied this program keep their own
                            values — edits here don't flow to them.
                        </Alert>
                    )}
                    {err && (
                        <Alert variant="danger" className="text-xs">
                            {err}
                        </Alert>
                    )}
                </div>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="ghost" size="sm" type="button">Cancel</Button>
                    </DialogClose>
                    {canCreateNew ? (
                        <Button
                            variant="primary"
                            size="sm"
                            disabled={working}
                            onClick={() => submit(true)}
                        >
                            {working ? 'Saving…' : 'Create new bank entry'}
                        </Button>
                    ) : (
                        <Button
                            variant="primary"
                            size="sm"
                            disabled={working}
                            onClick={() => submit(!linked)}
                        >
                            {working ? 'Saving…' : linked ? 'Save to bank' : 'Create bank entry'}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ─── retire program (soft-delete, cascades) ─────────────────────────────────

function RetireProgramDialog({
    program,
    onClose,
    onConfirm,
}: {
    program: ClientProgram;
    onClose: () => void;
    onConfirm: () => Promise<void>;
}) {
    const [working, setWorking] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const planned = program.status === 'Planned';
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>{planned ? 'Delete program?' : 'Discontinue program?'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-2">
                    <p className="text-sm text-text-muted">
                        {planned ? (
                            <>Delete <span className="font-medium text-text">{program.name}</span> permanently?</>
                        ) : (
                            <>Move <span className="font-medium text-text">{program.name}</span> to Discontinued?</>
                        )}
                    </p>
                    <Alert variant="warning" className="text-xs">
                        {planned
                            ? 'Planned programs have no data collected yet, so deletion is permanent. If the program is attached to a signed or active plan, delete will be blocked.'
                            : 'Any active or on-hold targets under this program will also be moved to Discontinued. Mastered targets stay as they are. You can bring everything back later from the "All" filter.'}
                    </Alert>
                </div>
                {err && <p className="mt-2 text-xs text-error">{err}</p>}
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="ghost" size="sm" type="button">Cancel</Button>
                    </DialogClose>
                    <Button
                        variant="danger"
                        size="sm"
                        disabled={working}
                        onClick={async () => {
                            setErr(null);
                            setWorking(true);
                            try {
                                await onConfirm();
                            } catch (e) {
                                const axErr = e as AxiosError<{ error?: string; blockingPlans?: Array<{ id: string; title: string; status: string }> }>;
                                const body = axErr?.response?.data;
                                if (body?.blockingPlans?.length) {
                                    const names = body.blockingPlans.map((p) => `${p.title} (${p.status})`).join(', ');
                                    setErr(`Cannot delete — attached to: ${names}`);
                                } else {
                                    setErr(body?.error || (extractErrorMessage(e, 'Could not proceed.')));
                                }
                            } finally {
                                setWorking(false);
                            }
                        }}
                    >
                        {working ? 'Working…' : planned ? 'Delete' : 'Discontinue'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ─── target form (create/edit) ──────────────────────────────────────────────

const DATA_TYPE_OPTIONS: Array<{ value: TargetDataType; label: string }> = [
    { value: 'trial', label: 'Trial (discrete-trial; % correct of N trials)' },
    { value: 'percent', label: 'Percent (% correct or completed)' },
    { value: 'frequency', label: 'Frequency (count per session or rate)' },
    { value: 'duration', label: 'Duration (seconds)' },
    { value: 'task_analysis', label: 'Task analysis (chained steps)' },
];

const DIRECTION_OPTIONS: Array<{ value: MasteryDirection; label: string }> = [
    { value: 'at_or_above', label: 'At or above' },
    { value: 'at_or_below', label: 'At or below' },
];

const CHAINING_OPTIONS: Array<{ value: ChainingMethod; label: string }> = [
    { value: 'total_task', label: 'Total task' },
    { value: 'forward', label: 'Forward' },
    { value: 'backward', label: 'Backward' },
];

interface TaStep { label: string }

function toTaSteps(raw: unknown[] | null): TaStep[] {
    if (!Array.isArray(raw)) return [];
    return raw.map((s) => {
        if (s && typeof s === 'object' && 'label' in (s as Record<string, unknown>)) {
            return { label: String((s as { label: unknown }).label ?? '') };
        }
        return { label: String(s ?? '') };
    });
}

function TargetFormDialog({
    initial,
    onClose,
    onSubmit,
}: {
    initial: ClientProgramTarget | null;
    onClose: () => void;
    onSubmit: (p: ClientProgramTargetWritePayload) => Promise<void>;
}) {
    const [name, setName] = useState(initial?.name ?? '');
    const [description, setDescription] = useState(initial?.description ?? '');
    const [dataType, setDataType] = useState<TargetDataType>(initial?.dataType ?? 'percent');
    const [trials, setTrials] = useState<string>(initial?.trials != null ? String(initial.trials) : '10');
    const [taSteps, setTaSteps] = useState<TaStep[]>(toTaSteps(initial?.taSteps ?? null));
    const [chainingMethod, setChaining] = useState<ChainingMethod>(initial?.chainingMethod ?? 'total_task');
    const [masteryDirection, setDir] = useState<MasteryDirection>(initial?.masteryDirection ?? 'at_or_above');
    const [masteryValue, setValue] = useState<string>(initial?.masteryValue != null ? String(initial.masteryValue) : '80');
    const [masterySessions, setMSess] = useState<string>(initial?.masterySessions != null ? String(initial.masterySessions) : '3');
    const [generalization, setGeneralization] = useState(initial?.generalization ?? '');
    const [baselineDate, setBaselineDate] = useState(initial?.baselineDate ?? '');
    const [baseline, setBaseline] = useState(initial?.baseline ?? '');
    const [status, setStatus] = useState<ClientTargetStatus>(initial?.status ?? 'In Progress');

    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    const masteryUnit = unitForDataType(dataType);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setErr(null);
        if (!name.trim()) { setErr('Name is required'); return; }

        const payload: ClientProgramTargetWritePayload = {
            name: name.trim(),
            description: description.trim(),
            dataType,
            masteryDirection,
            masteryValue: masteryValue === '' ? null : Number(masteryValue),
            masterySessions: masterySessions === '' ? null : Number(masterySessions),
            generalization: generalization.trim(),
            baselineDate: baselineDate || null,
            baseline: baseline.trim(),
            trials: null,
            taSteps: null,
            chainingMethod: null,
            status,
        };

        if (dataType === 'trial') {
            if (trials === '' || Number(trials) <= 0) { setErr('Trials per session is required'); return; }
            payload.trials = Number(trials);
        } else if (dataType === 'task_analysis') {
            const cleaned = taSteps.map((s) => ({ label: s.label.trim() })).filter((s) => s.label);
            if (cleaned.length === 0) { setErr('Task analysis requires at least one step'); return; }
            payload.taSteps = cleaned;
            payload.chainingMethod = chainingMethod;
        }

        setSaving(true);
        try {
            await onSubmit(payload);
            onClose();
        } catch (e2) {
            setErr(extractErrorMessage(e2, 'Could not save.'));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="w-[min(calc(100vw-2rem),48rem)] max-h-[85vh] flex flex-col" aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>{initial ? 'Edit target' : 'Add target'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-1 -mx-1">
                    <FormField label="Name" required error={err ?? undefined}>
                        {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}
                    </FormField>
                    <FormField label="Description">
                        {(id) => <Textarea id={id} value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />}
                    </FormField>
                    <FormField label="Data type" required>
                        {(id) => (
                            <select
                                id={id}
                                value={dataType}
                                onChange={(e) => setDataType(e.target.value as TargetDataType)}
                                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none"
                            >
                                {DATA_TYPE_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        )}
                    </FormField>

                    {dataType === 'trial' && (
                        <FormField label="Trials per session" required>
                            {(id) => (
                                <Input
                                    id={id}
                                    type="number"
                                    min={1}
                                    value={trials}
                                    onChange={(e) => setTrials(e.target.value)}
                                />
                            )}
                        </FormField>
                    )}

                    {dataType === 'task_analysis' && (
                        <>
                            <FormField label="Chaining method" required>
                                {(id) => (
                                    <select
                                        id={id}
                                        value={chainingMethod}
                                        onChange={(e) => setChaining(e.target.value as ChainingMethod)}
                                        className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none"
                                    >
                                        {CHAINING_OPTIONS.map((o) => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                    </select>
                                )}
                            </FormField>
                            <TaStepsEditor steps={taSteps} onChange={setTaSteps} />
                        </>
                    )}

                    <div className="grid grid-cols-3 gap-x-3">
                        <FormField label="Direction">
                            {(id) => (
                                <select
                                    id={id}
                                    value={masteryDirection}
                                    onChange={(e) => setDir(e.target.value as MasteryDirection)}
                                    className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none"
                                >
                                    {DIRECTION_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                            )}
                        </FormField>
                        <FormField label={masteryUnit ? `Mastery (${masteryUnit})` : 'Mastery value'}>
                            {(id) => (
                                <Input
                                    id={id}
                                    type="number"
                                    step="any"
                                    value={masteryValue}
                                    onChange={(e) => setValue(e.target.value)}
                                />
                            )}
                        </FormField>
                        <FormField label="Consecutive sessions">
                            {(id) => (
                                <Input
                                    id={id}
                                    type="number"
                                    min={1}
                                    value={masterySessions}
                                    onChange={(e) => setMSess(e.target.value)}
                                />
                            )}
                        </FormField>
                    </div>

                    <FormField label="Baseline">
                        {(id) => <Textarea id={id} value={baseline} onChange={(e) => setBaseline(e.target.value)} rows={2} />}
                    </FormField>
                    <FormField label="Baseline date">
                        {(id) => <Input id={id} type="date" min="1900-01-01" max={toIsoDate(new Date())} value={baselineDate} onChange={(e) => setBaselineDate(e.target.value)} />}
                    </FormField>
                    <FormField label="Generalization">
                        {(id) => <Textarea id={id} value={generalization} onChange={(e) => setGeneralization(e.target.value)} rows={2} />}
                    </FormField>

                    {initial && (
                        <FormField label="Status">
                            {(id) => (
                                <select
                                    id={id}
                                    value={status}
                                    onChange={(e) => setStatus(e.target.value as ClientTargetStatus)}
                                    className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none max-w-[12rem]"
                                >
                                    <option value="In Progress">In Progress</option>
                                    <option value="On Hold">On Hold</option>
                                    <option value="Mastered">Mastered</option>
                                    <option value="Discontinued">Discontinued</option>
                                </select>
                            )}
                        </FormField>
                    )}

                    <DialogFooter className="mt-2">
                        <DialogClose asChild>
                            <Button variant="ghost" size="sm" type="button">Cancel</Button>
                        </DialogClose>
                        <Button variant="primary" size="sm" type="submit" disabled={saving}>
                            {saving ? 'Saving…' : initial ? 'Save' : 'Create'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function TaStepsEditor({
    steps,
    onChange,
}: {
    steps: TaStep[];
    onChange: (next: TaStep[]) => void;
}) {
    function update(i: number, label: string) {
        const next = steps.slice();
        next[i] = { label };
        onChange(next);
    }
    function add() { onChange([...steps, { label: '' }]); }
    function remove(i: number) { onChange(steps.filter((_, idx) => idx !== i)); }
    function moveUp(i: number) {
        if (i === 0) return;
        const next = steps.slice();
        [next[i - 1], next[i]] = [next[i], next[i - 1]];
        onChange(next);
    }
    function moveDown(i: number) {
        if (i === steps.length - 1) return;
        const next = steps.slice();
        [next[i], next[i + 1]] = [next[i + 1], next[i]];
        onChange(next);
    }

    return (
        <div className="mb-3.5">
            <div className="block text-xs font-bold text-text-muted uppercase tracking-wider mb-1.5">
                Task steps
                <span className="text-error ml-0.5">*</span>
            </div>
            <div className="space-y-1.5">
                {steps.length === 0 && (
                    <p className="text-xs text-text-subtle italic">No steps yet. Add at least one.</p>
                )}
                {steps.map((s, i) => (
                    <div key={i} className="flex items-center gap-2">
                        <span className="text-xs text-text-subtle tabular-nums w-5 text-right">{i + 1}.</span>
                        <Input
                            value={s.label}
                            onChange={(e) => update(i, e.target.value)}
                            placeholder="Step description"
                            className="h-8 py-1 flex-1"
                        />
                        <Button type="button" variant="ghost" size="xs" onClick={() => moveUp(i)} disabled={i === 0} aria-label="Move up">↑</Button>
                        <Button type="button" variant="ghost" size="xs" onClick={() => moveDown(i)} disabled={i === steps.length - 1} aria-label="Move down">↓</Button>
                        <Button type="button" variant="ghost" size="xs" onClick={() => remove(i)} aria-label="Remove step">×</Button>
                    </div>
                ))}
            </div>
            <Button type="button" variant="ghost" size="xs" onClick={add} className="mt-1.5">
                <Plus className="w-3 h-3" /> Add step
            </Button>
        </div>
    );
}

// ─── save target to bank ────────────────────────────────────────────────────

function SaveTargetToBankDialog({
    target,
    onClose,
    onSubmit,
}: {
    target: ClientProgramTarget;
    onClose: () => void;
    onSubmit: (createNew: boolean) => Promise<void>;
}) {
    const [working, setWorking] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const [canCreateNew, setCanCreateNew] = useState(false);
    const [parentBlocked, setParentBlocked] = useState(false);

    const linked = !!target.sourceBankId;

    async function submit(createNew: boolean) {
        setErr(null);
        setWorking(true);
        try {
            await onSubmit(createNew);
            onClose();
        } catch (e) {
            if (e instanceof AxiosError) {
                const data = e.response?.data as { error?: string; canCreateNew?: boolean } | undefined;
                setErr(data?.error ?? 'Could not save to bank');
                if (data?.canCreateNew) setCanCreateNew(true);
                // 400 parentNotLinked / 409 parentBankArchived are blocking — user
                // must fix the parent program before retrying, so hide the CTA.
                if (e.response?.status === 400 || e.response?.status === 409) {
                    const msg = (data?.error ?? '').toLowerCase();
                    if (msg.includes('parent') || msg.includes('program')) {
                        setParentBlocked(true);
                        setCanCreateNew(false);
                    }
                }
            } else {
                setErr(extractErrorMessage(e, 'Could not save to bank'));
            }
        } finally {
            setWorking(false);
        }
    }

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>Save target to bank?</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 text-sm">
                    <p className="text-text-muted">
                        {linked
                            ? <>Push the current values of <span className="font-medium text-text">{target.name}</span> to the linked bank target, replacing what's there.</>
                            : <>This target isn't linked to the bank yet. Saving will create a new bank target under the parent program's bank entry and link this target to it.</>
                        }
                    </p>
                    <Alert variant="warning" className="text-xs">
                        The parent program must already be linked to a bank entry. Save-to-bank
                        updates <strong>one bank target only</strong> — other clients keep their
                        own values.
                    </Alert>
                    {err && (
                        <Alert variant="danger" className="text-xs">
                            {err}
                        </Alert>
                    )}
                </div>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="ghost" size="sm" type="button">
                            {parentBlocked ? 'Close' : 'Cancel'}
                        </Button>
                    </DialogClose>
                    {!parentBlocked && (
                        canCreateNew ? (
                            <Button
                                variant="primary"
                                size="sm"
                                disabled={working}
                                onClick={() => submit(true)}
                            >
                                {working ? 'Saving…' : 'Create new bank target'}
                            </Button>
                        ) : (
                            <Button
                                variant="primary"
                                size="sm"
                                disabled={working}
                                onClick={() => submit(!linked)}
                            >
                                {working ? 'Saving…' : linked ? 'Save to bank' : 'Create bank target'}
                            </Button>
                        )
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ─── retire target ──────────────────────────────────────────────────────────

function RetireTargetDialog({
    target,
    onClose,
    onConfirm,
}: {
    target: ClientProgramTarget;
    onClose: () => void;
    onConfirm: () => Promise<void>;
}) {
    const [working, setWorking] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>Discontinue target?</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-text-muted">
                    Move <span className="font-medium text-text">{target.name}</span> to
                    Discontinued? The record stays on the program's history. You can bring it
                    back later by toggling "Show retired".
                </p>
                {err && <p className="mt-2 text-xs text-error">{err}</p>}
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="ghost" size="sm" type="button">Cancel</Button>
                    </DialogClose>
                    <Button
                        variant="danger"
                        size="sm"
                        disabled={working}
                        onClick={async () => {
                            setErr(null);
                            setWorking(true);
                            try {
                                await onConfirm();
                            } catch (e) {
                                setErr(extractErrorMessage(e, 'Could not discontinue.'));
                            } finally {
                                setWorking(false);
                            }
                        }}
                    >
                        {working ? 'Working…' : 'Discontinue'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ─── draggable target row ───────────────────────────────────────────────────

function ClientTargetRow({
    target,
    canEdit,
    draggable,
    clientId,
    programRetired,
    onEdit,
    onSaveToBank,
    onRetire,
}: {
    target: ClientProgramTarget;
    canEdit: boolean;
    draggable: boolean;
    clientId: string;
    programRetired: boolean;
    onEdit: () => void;
    onSaveToBank: () => void;
    onRetire: () => void;
}) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: target.id,
        disabled: !draggable,
    });
    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : undefined,
    };
    const targetRetired = target.status === 'Discontinued' || target.status === 'Mastered';
    return (
        <tr
            ref={setNodeRef}
            style={style}
            className={`border-b border-border last:border-b-0 align-top transition-colors ${targetRetired ? 'opacity-60 hover:bg-surface-muted' : 'hover:bg-surface-muted'
                }`}
        >
            {canEdit && (
                <td className="px-2 py-1.5 w-7 text-text-subtle">
                    {draggable ? (
                        <button
                            type="button"
                            {...attributes}
                            {...listeners}
                            className="cursor-grab active:cursor-grabbing p-1 -m-1 rounded hover:bg-surface focus:outline-none focus:ring-1 focus:ring-accent"
                            aria-label={`Drag ${target.name}`}
                        >
                            <GripVertical className="w-3.5 h-3.5" />
                        </button>
                    ) : (
                        <span className="inline-block w-3.5 h-3.5" aria-hidden />
                    )}
                </td>
            )}
            <td className="px-3 py-1.5 font-medium">
                {target.name}
                {target.description && (
                    <div className="text-2xs text-text-subtle font-normal mt-0.5 line-clamp-2">
                        {target.description}
                    </div>
                )}
            </td>
            <td className="px-3 py-1.5 text-text-muted whitespace-nowrap">
                {formatDataType(target.dataType)}
            </td>
            <td className="px-3 py-1.5 text-text-muted">{formatMastery(target)}</td>
            <td className="px-3 py-1.5"><StatusBadge status={target.status} /></td>
            <td className="px-3 py-1.5 text-2xs text-text-subtle whitespace-nowrap">
                {target.sourceBankId ? 'Linked' : 'Custom'}
            </td>
            {canEdit && (
                <td className="px-3 py-1.5 text-right whitespace-nowrap">
                    <Button
                        variant="ghost"
                        size="xs"
                        aria-label={`View graph for ${target.name}`}
                        title="View graph"
                        asChild
                        className="mr-1"
                    >
                        <Link to={`/data-analysis/${clientId}?targetId=${target.id}`}>
                            <BarChart3 className="w-3 h-3" />
                        </Link>
                    </Button>
                    {!targetRetired && !programRetired && (
                        <>
                            <Button
                                variant="ghost"
                                size="xs"
                                aria-label={`Edit ${target.name}`}
                                onClick={onEdit}
                                className="mr-1"
                            >
                                <Pencil className="w-3 h-3" />
                            </Button>
                            <Button
                                variant="ghost"
                                size="xs"
                                aria-label={`Save ${target.name} to bank`}
                                title="Save to bank"
                                onClick={onSaveToBank}
                                className="mr-1"
                            >
                                <Upload className="w-3 h-3" />
                            </Button>
                            <Button
                                variant="ghost"
                                size="xs"
                                aria-label={`Discontinue ${target.name}`}
                                title="Discontinue"
                                onClick={onRetire}
                            >
                                <Archive className="w-3 h-3" />
                            </Button>
                        </>
                    )}
                </td>
            )}
        </tr>
    );
}

// ─── formatters (shared with program bank detail UI) ────────────────────────

const DATA_TYPE_LABEL: Record<TargetDataType, string> = {
    trial: 'Trial',
    percent: 'Percent',
    frequency: 'Frequency',
    duration: 'Duration',
    task_analysis: 'Task analysis',
};

function formatDataType(t: TargetDataType): string {
    return DATA_TYPE_LABEL[t] ?? t;
}

function formatMastery(t: ClientProgramTarget): string {
    if (t.masteryValue == null) return '—';
    const unit = unitForDataType(t.dataType);
    const comparator = t.masteryDirection === 'at_or_below' ? 'at or below' : 'at or above';
    const value = `${t.masteryValue}${unit}`;
    const sessions = t.masterySessions
        ? ` over ${t.masterySessions} session${t.masterySessions === 1 ? '' : 's'}`
        : '';
    return `${comparator} ${value}${sessions}`;
}

function unitForDataType(dataType: TargetDataType): string {
    switch (dataType) {
        case 'trial':
        case 'percent':
        case 'task_analysis':
            return '%';
        case 'duration':
            return 's';
        case 'frequency':
            return '';
        default:
            return '';
    }
}
